import { useEffect, useRef, useState, useCallback } from 'react';

const IDLE_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
const WARNING_DURATION = 60_000; // 60 seconds warning before logout

interface UseIdleTimeoutOptions {
  timeoutMs?: number; // default 30 minutes
  onIdle: () => void;
  enabled?: boolean;
}

export const useIdleTimeout = ({ timeoutMs = 30 * 60 * 1000, onIdle, enabled = true }: UseIdleTimeoutOptions) => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(WARNING_DURATION / 1000);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    clearAllTimers();
    setShowWarning(false);
    setRemainingSeconds(WARNING_DURATION / 1000);

    // Start idle timer — when it fires, show the warning
    idleTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSeconds(WARNING_DURATION / 1000);

      // Start countdown
      let secondsLeft = WARNING_DURATION / 1000;
      countdownRef.current = setInterval(() => {
        secondsLeft -= 1;
        setRemainingSeconds(secondsLeft);
        if (secondsLeft <= 0) {
          clearAllTimers();
          setShowWarning(false);
          onIdle();
        }
      }, 1000);
    }, timeoutMs - WARNING_DURATION);
  }, [enabled, timeoutMs, onIdle, clearAllTimers]);

  const stayActive = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) return;

    resetTimer();

    const handleActivity = () => {
      // Only reset if warning is NOT showing — once warning is up, user must click "Stay"
      if (!showWarning) {
        resetTimer();
      }
    };

    IDLE_EVENTS.forEach(event => document.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      clearAllTimers();
      IDLE_EVENTS.forEach(event => document.removeEventListener(event, handleActivity));
    };
  }, [enabled, resetTimer, clearAllTimers, showWarning]);

  return { showWarning, remainingSeconds, stayActive };
};
